import {
  Deposit,
  DepositRequest,
  DepositRequestTest,
  PaymentLink,
  ProductsLocalization,
  PurchaseOrder,
  PurchaseProduct,
} from 'data/interfaces/Common';
import {
  AccessAuthentication,
  OrderApi,
  ProfilePromoCodesApi,
  PromoCodeResponseDto,
} from '@arbihunter/api-client';
import { InterfaceLanguage, ProductType } from 'types/enums';
import request from 'utils/request';
import { ProfileType } from '../interfaces/IRegistration';
import { api } from 'shared/api';

export default class PurchaseRepository {
  static async getOrders(profile: ProfileType, token: string): Promise<PurchaseOrder[]> {
    const res = await request({
      method: 'get',
      url: '/api/order',
      profile,
      token,
    });
    if (res?.err) {
      return null;
    }
    return res;
  }

  static async createOrder(
    token: string,
    productId: number,
    promoCode?: string,
    locale: string = 'ru',
  ): Promise<PurchaseOrder> {
    try {
      const promoCodes = await api.run(OrderApi).createOrder0(
        {
          id: productId,
          // @ts-expect-error locale type
          locale: locale,
          createOrderQueryDto: promoCode != null ? { promoCode } : {},
        },
        { authMethods: { default: new AccessAuthentication({ getToken: () => token }) } },
      );
      return promoCodes;
    } catch (error) {
      console.error('Ошибка загрузки промокодов:', error);
      return null;
    }
  }

  static async createDeposit(data: DepositRequest): Promise<Deposit> {
    const res = await request({
      method: 'post',
      url: '/api/payments/passimpay/deposit',
      data,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async createDepositTest(data: DepositRequestTest): Promise<Deposit> {
    const res = await request({
      method: 'post',
      url: '/api/payments/passimpay-test/deposit',
      data,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async getPaymentLinksFromId(token: string, id: number): Promise<PaymentLink> {
    const res = await request({
      method: 'get',
      url: `/api/payments/passimpay/link?orderIds=${id}`,
      token,
    });
    if (res.err || res.length === 0) {
      return null;
    }
    return res[0];
  }

  static async getProducts(
    profile: ProfileType,
    token: string,
    type: ProductType,
    lang: InterfaceLanguage,
  ): Promise<PurchaseProduct[] | null> {
    const res = await request({
      method: 'get',
      url: `/api/product?lang=${lang}&type=${type}`,
      profile,
      token,
    });
    if (res?.err) {
      return null;
    }
    return res;
  }

  static async getProductById(
    profile: ProfileType,
    token: string,
    id: number,
    lang: InterfaceLanguage,
    promoCode?: string,
  ): Promise<PurchaseProduct | null> {
    const res = await request({
      method: 'get',
      url: `/api/product/${id}`,
      profile,
      token,
      data: {
        locale: lang,
        promoCode,
      },
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async getProductsLocalization(
    profile: ProfileType,
    token: string,
    lang: InterfaceLanguage = InterfaceLanguage.Russian,
  ): Promise<ProductsLocalization[] | null> {
    const res = await request({
      method: 'get',
      url: `/api/product/localization`,
      profile,
      token,
      data: {
        language: lang,
      },
    });
    if (res?.err) {
      return null;
    }
    return res;
  }

  static async getAvailablePromoCodes(token: string): Promise<PromoCodeResponseDto[] | []> {
    try {
      const promoCodes = await api.run(ProfilePromoCodesApi).getAvailablePromoCodes0(
        {
          limit: 1000,
          page: 0,
        },
        {
          authMethods: { default: new AccessAuthentication({ getToken: () => token }) },
        },
      );

      return promoCodes.data?.map((p) => JSON.parse(JSON.stringify(p))) ?? [];
    } catch (error) {
      console.error('Ошибка загрузки промокодов:', error);
      return [];
    }
  }

  static async getPromoCode(token: string, code: string): Promise<PromoCodeResponseDto | null> {
    try {
      const response = await api.run(ProfilePromoCodesApi).getPromoCode0(
        { code },
        {
          authMethods: { default: new AccessAuthentication({ getToken: () => token }) },
        },
      );
      return response;
    } catch (error) {
      console.error('Ошибка при проверке промокода:', error);
      return null;
    }
  }
}
